import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';
import PageHeader from '../components/PageHeader';
import Panel from '../components/Panel';
import StatusChip from '../components/StatusChip';
import { ApplicationSteps, roleTone, plaidIdvSessionUrl, idvStatusTone, idvStatusLabel } from './LeaseDetail/ApplicationPreviewPanel';
import { formatIdvFailureDescription } from './LeaseDetail/idvFailureReason';
import {
  applySelectionToApplication,
  canCancelApplication,
  FINALIZE_POLL_MS,
  findTerm,
  findUnit,
  formatTermLabel,
  formatUnitLabel,
  isoToday,
  minMoveInDate,
  resolveSelection,
  shouldPollApplication,
} from './applicationSelection';
import { api } from '../shared/service/dataprovider';
import ResetPasswordConfirmModal from './LeaseDetail/ResetPasswordConfirmModal';
import ResetPasswordModal from './LeaseDetail/ResetPasswordModal';

const blank = value => (value === null || value === undefined || value === '' ? 'N.A.' : value);
const formatDate = value => (value ? moment(value).format('MM/DD/YYYY') : 'N.A.');
const formatDateTime = value => (value ? moment(value).format('MM/DD/YYYY h:mm A') : 'N.A.');
const formatInputDate = value => (value && moment(value).isValid() ? moment(value).format('YYYY-MM-DD') : '');
const formatMoney = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : 'N.A.';
};
const humanize = value =>
  value
    ? String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase())
    : 'N.A.';

// Reason for an application that has not (yet) become a lease. Colours mirror
// the severity: a terminal finalize crash is an error, a stalled/awaiting one a
// warning/info, a plain in-progress application is neutral.
const REASON_TONES = {
  leased: 'success',
  finalize_failed: 'error',
  finalizing: 'warning',
  pending_resubmit: 'info',
  pending: 'neutral',
  cancelled: 'neutral',
};

const stateTone = value => {
  if (value === 'finalize_failed') return 'error';
  if (value === 'finalizing') return 'warning';
  if (value === 'finalized') return 'success';
  return 'neutral';
};

function Field({ label, children }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </Grid>
  );
}

export function ApplicationSelectionEditor({ application, queryKey, onFinalizeQueued }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [unitNumber, setUnitNumber] = useState(application.unit ?? '');
  const [moveInDate, setMoveInDate] = useState(formatInputDate(application.move_in_date));
  const [term, setTerm] = useState(application.term != null ? String(application.term) : '');

  const canEdit =
    !application.lease_id &&
    application.submission_status !== 'completed' &&
    application.finalization_state !== 'finalized' &&
    application.finalization_state !== 'finalizing';
  const willRetryPaidFinalize = application.finalization_state === 'finalize_failed' && application.payment_status === 'paid';

  const availabilityQuery = useQuery({
    queryKey: ['application-availability', application.property_id, moveInDate],
    queryFn: () =>
      api.get('/public/availability/search', {
        params: { propertyId: application.property_id, moveInDate, perPage: 200 },
      }),
    enabled: Boolean(canEdit && application.property_id && moveInDate),
  });

  const units = [...(availabilityQuery.data?.data?.units ?? [])].sort((a, b) =>
    String(a.unitNumber).localeCompare(String(b.unitNumber), undefined, { numeric: true })
  );
  const selectedUnit = findUnit(units, unitNumber);
  const effectiveTerm = findTerm(selectedUnit, term);
  const { canSave } = resolveSelection({ units, unitNumber, term, moveInDate });
  const today = isoToday();
  const minMoveIn = minMoveInDate(selectedUnit, today);

  const selectionMutation = useMutation({
    mutationFn: payload => api.put(`/v1/admin/applications/${application.application_id}/selection`, payload),
    onSuccess: async ({ data }) => {
      toast.success(
        data?.finalizeEnqueued
          ? t('applicationDetail.selectionLeaseQueued', 'Selection saved. Lease creation was queued.')
          : t('applicationDetail.selectionSaved', 'Application selection saved.')
      );
      queryClient.setQueryData(queryKey, prev => {
        if (!prev?.data?.application) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            application: applySelectionToApplication(prev.data.application, data),
          },
        };
      });
      if (data?.finalizeEnqueued) onFinalizeQueued?.();
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: ['application-availability', application.property_id] });
    },
    onError: e => toast.error(e?.response?.data?.message ?? t('applicationDetail.selectionSaveFailed', 'Could not save application selection.')),
  });

  if (!canEdit) return null;

  const busy = availabilityQuery.isFetching || selectionMutation.isPending;
  const saveDisabled = busy || !canSave;
  const unitSelectValue = selectedUnit ? unitNumber : '';
  const termSelectValue = effectiveTerm ? String(effectiveTerm.term) : '';

  const onMoveInChange = nextDate => {
    setMoveInDate(nextDate);
    setTerm('');
  };
  const onUnitChange = nextUnit => {
    setUnitNumber(nextUnit);
    setTerm('');
  };

  return (
    <Panel title={t('applicationDetail.selectionTitle', 'Application selection')} sx={{ mb: 2.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          'applicationDetail.selectionDescription',
          'Pick an available unit and priced lease term, then save. If payment is already approved, that also queues lease creation.'
        )}
      </Typography>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label={t('applicationDetail.moveInDate', 'Move-in Date')}
            value={moveInDate}
            onChange={e => onMoveInChange(e.target.value)}
            disabled={selectionMutation.isPending}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minMoveIn }}
            helperText={
              selectedUnit?.madeReadyDate && selectedUnit.madeReadyDate > today
                ? t('applicationDetail.unitAvailableFrom', 'Unit available from {{date}}', {
                    date: moment(selectedUnit.madeReadyDate).format('MMM D'),
                  })
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small" disabled={busy || !units.length}>
            <InputLabel id="application-unit-label" shrink>
              {t('applicationDetail.unit', 'Unit')}
            </InputLabel>
            <Select
              labelId="application-unit-label"
              label={t('applicationDetail.unit', 'Unit')}
              value={unitSelectValue}
              displayEmpty
              renderValue={value => {
                if (!value) {
                  return (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {t('applicationDetail.selectUnit', 'Select unit')}
                    </Box>
                  );
                }
                const option = findUnit(units, value);
                return option ? formatUnitLabel(option) : value;
              }}
              onChange={e => onUnitChange(e.target.value)}
            >
              <MenuItem value="" disabled>
                {t('applicationDetail.selectUnit', 'Select unit')}
              </MenuItem>
              {units.map(u => (
                <MenuItem key={u.unitNumber} value={u.unitNumber}>
                  {formatUnitLabel(u)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small" disabled={busy || !selectedUnit?.matchedTerms?.length}>
            <InputLabel id="application-term-label" shrink>
              {t('applicationDetail.leaseTerm', 'Lease Term')}
            </InputLabel>
            <Select
              labelId="application-term-label"
              label={t('applicationDetail.leaseTerm', 'Lease Term')}
              value={termSelectValue}
              displayEmpty
              renderValue={value => {
                if (!value) {
                  return (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {t('applicationDetail.selectLeaseTerm', 'Select lease term')}
                    </Box>
                  );
                }
                const option = findTerm(selectedUnit, value);
                return option ? formatTermLabel(option) : value;
              }}
              onChange={e => setTerm(e.target.value ? String(e.target.value) : '')}
            >
              <MenuItem value="" disabled>
                {t('applicationDetail.selectLeaseTerm', 'Select lease term')}
              </MenuItem>
              {(selectedUnit?.matchedTerms ?? []).map(termOption => (
                <MenuItem key={termOption.term} value={String(termOption.term)}>
                  {formatTermLabel(termOption)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={1.5}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {t('applicationDetail.rent', 'Rent')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {effectiveTerm ? formatMoney(effectiveTerm.effectiveRent) : formatMoney(application.rent)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={1.5}>
          <Button
            variant="contained"
            onClick={() => selectionMutation.mutate({ unitNumber: selectedUnit.unitNumber, moveInDate, term: String(effectiveTerm.term) })}
            disabled={saveDisabled}
            sx={{ minWidth: 96 }}
          >
            {selectionMutation.isPending ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </Button>
        </Grid>
      </Grid>
      {availabilityQuery.isFetching ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            {t('applicationDetail.loadingAvailability', 'Loading available units and lease terms…')}
          </Typography>
        </Stack>
      ) : null}
      {availabilityQuery.isError ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {t('applicationDetail.availabilityLoadError', 'Could not load available units for this move-in date.')}
        </Alert>
      ) : null}
      {availabilityQuery.isSuccess && units.length === 0 ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {t('applicationDetail.noUnitsForDate', 'No units are available for the selected move-in date. Please try a different date.')}
        </Alert>
      ) : null}
      {availabilityQuery.isSuccess && unitNumber && !selectedUnit ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {t(
            'applicationDetail.unitNotAvailable',
            'Unit {{unit}} is not currently available. It may be leased or the move-in date is outside its pricing window.',
            { unit: unitNumber }
          )}
        </Alert>
      ) : null}
      {selectedUnit && selectedUnit.matchedTerms.length === 0 ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {t('applicationDetail.unitNoTerms', 'This unit is not available for the selected move-in date. Please try a different date.')}
        </Alert>
      ) : null}
      {selectedUnit && selectedUnit.matchedTerms.length > 0 && !effectiveTerm ? (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {t('applicationDetail.selectLeaseTermToContinue', 'Select a lease term to continue.')}
        </Alert>
      ) : null}
      {willRetryPaidFinalize ? (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1.5 }}>
          {t(
            'applicationDetail.selectionRetryNotice',
            'This paid application failed finalization. Saving a valid selection will reopen finalization and queue another attempt.'
          )}
        </Typography>
      ) : null}
    </Panel>
  );
}

export default function ApplicationDetail({ applicationId: applicationIdProp, embedded = false } = {}) {
  const { t } = useTranslation();
  const { id: idFromParams } = useParams();
  const id = applicationIdProp || idFromParams;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [showResetPasswordConfirmModal, setShowResetPasswordConfirmModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [inviteUrl, setInviteUrl] = useState(null);

  const queryKey = ['application', id];
  const finalizeQueuedAt = useRef(null);
  const [awaitingFinalize, setAwaitingFinalize] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => api.get(`/v1/admin/applications/${id}`),
    enabled: Boolean(id),
    retry: false,
    refetchInterval: query => {
      const app = query.state.data?.data?.application;
      return shouldPollApplication(app, finalizeQueuedAt.current) ? FINALIZE_POLL_MS : false;
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/v1/admin/applications/${id}/cancel`),
    onSuccess: () => {
      setCancelOpen(false);
      toast.success(t('applicationDetail.cancelSuccess', 'Application cancelled. The applicant can re-apply.'));
      queryClient.invalidateQueries({ queryKey });
    },
    onError: e => toast.error(e?.response?.data?.message ?? t('applicationDetail.cancelFailed', 'Could not cancel this application.')),
  });

  const retryFinalizeMutation = useMutation({
    mutationFn: () => api.post(`/v1/admin/applications/${id}/retry-finalize`),
    onSuccess: () => {
      toast.success(t('applicationDetail.retryFinalizeQueued', 'Lease creation was queued again.'));
      finalizeQueuedAt.current = Date.now();
      setAwaitingFinalize(true);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: e => toast.error(e?.response?.data?.message ?? t('applicationDetail.retryFinalizeFailed', 'Could not retry finalization.')),
  });

  const application = data?.data?.application;
  const relatedUser = data?.data?.relatedUser ?? null;
  const resumeUrl = data?.data?.resumeUrl ?? null;

  useEffect(() => {
    const fromApi = data?.data?.inviteUrl ?? null;
    if (fromApi) setInviteUrl(fromApi);
  }, [data?.data?.inviteUrl]);

  const resetPasswordEmailMutation = useMutation({
    mutationFn: payload => api.post(`/v1/admin/reset-password-email`, payload),
    onSuccess: () => {
      toast.success(
        t('applicationDetail.resetPasswordSent', 'Reset password email sent to {{email}}.', {
          email: relatedUser?.email,
        })
      );
    },
    onError: e => toast.error(e?.response?.data?.message ?? t('applicationDetail.resetPasswordFailed', 'Could not send reset password email.')),
  });

  const inviteApplicationMutation = useMutation({
    mutationFn: () => api.post('/v1/admin/invite-application-user', { applicationId: id }),
    onSuccess: res => {
      setInviteUrl(res?.data?.inviteUrl ?? null);
      toast.success(t('applicationDetail.inviteSent', 'Invitation sent.'));
    },
    onError: e => {
      const msg = e?.response?.data?.err ?? e?.response?.data?.message ?? t('applicationDetail.inviteFailed', 'Could not send invitation.');
      toast.error(msg);
    },
  });

  const handleCopyInviteLink = () => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl).then(
      () => toast.success(t('applicationDetail.inviteLinkCopied', 'Invite link copied.')),
      () => toast.error(t('applicationDetail.inviteLinkCopyFailed', 'Could not copy invite link.'))
    );
  };

  const handleCopyResumeLink = () => {
    if (!resumeUrl) return;
    navigator.clipboard.writeText(resumeUrl).then(
      () => toast.success(t('applicationDetail.resumeLinkCopied', 'Resume link copied.')),
      () => toast.error(t('applicationDetail.resumeLinkCopyFailed', 'Could not copy resume link.'))
    );
  };
  useEffect(() => {
    if (!awaitingFinalize || !application) return;
    if (application.lease_id || application.finalization_state === 'finalized' || application.finalization_state === 'finalize_failed') {
      finalizeQueuedAt.current = null;
      setAwaitingFinalize(false);
    }
  }, [application, awaitingFinalize]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !application) {
    const status = error?.response?.status;
    const message =
      status === 403
        ? t('applicationDetail.forbidden', 'You do not have access to this application.')
        : status === 404
          ? t('applicationDetail.notFound', 'Application not found.')
          : t('applicationDetail.loadError', 'Could not load this application.');
    return (
      <Box>
        <PageHeader title={t('applicationDetail.title', 'Application')} />
        <Panel>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
          <Link component={RouterLink} to="/reports" underline="hover" sx={{ display: 'inline-block', mt: 1.5 }}>
            {t('applicationDetail.backToReports', 'Back to Applicants')}
          </Link>
        </Panel>
      </Box>
    );
  }

  const reason = application.pending_reason;
  const roster = data?.data?.roster ?? [];
  const submitted = data?.data?.submittedApplication;
  const siblings = data?.data?.siblingApplications ?? [];
  // Plaid IDV sessions for this applicant, newest first (see admin
  // /applications/:id). The status panel surfaces the latest one — most usefully
  // a FAILED verification — with a link to the full history in the submitted view.
  const idvSessions = data?.data?.idvSessions ?? [];
  const latestIdv = idvSessions[0] ?? null;
  const latestIdvFailureDescription = formatIdvFailureDescription(latestIdv);
  const canCancel = canCancelApplication(application);
  // Other open applications for this person — staff jump links when an applicant
  // has multiple pending rows (common with re-apply / duplicate shells).
  const OPEN_REASONS = new Set(['pending', 'pending_resubmit', 'finalizing', 'finalize_failed']);
  const pendingSiblings = siblings.filter(sib => !sib.lease_id && OPEN_REASONS.has(sib.pending_reason || 'pending'));

  return (
    <Box>
      {!embedded ? (
        <Link
          component="button"
          type="button"
          onClick={() => navigate('/reports')}
          underline="hover"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          {t('applicationDetail.backToReports', 'Back to Applicants')}
        </Link>
      ) : null}

      <PageHeader
        title={blank(application.name)}
        description={blank(application.property_name)}
        action={<StatusChip status={reason} label={humanize(reason)} tone={REASON_TONES[reason] || 'neutral'} />}
      />

      {pendingSiblings.length > 0 ? (
        <Panel
          title={t('applicationDetail.pendingApplicationsTitle', 'Other pending applications ({{count}})', { count: pendingSiblings.length })}
          sx={{ mb: 2.5 }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(
              'applicationDetail.pendingApplicationsHint',
              'This applicant has more than one open application. Open any row below to jump straight to that record.'
            )}
          </Typography>
          <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2.5 }}>
            {pendingSiblings.map(sib => (
              <Box component="li" key={sib.application_id}>
                <Link component={RouterLink} to={`/applications/${sib.application_id}`} underline="hover">
                  {blank(sib.property_name)}
                  {sib.unit ? ` · Unit ${sib.unit}` : ''}
                  {' — '}
                  {humanize(sib.pending_reason)}
                  {sib.has_data ? '' : ` (${t('applicationDetail.otherAppsNoData', 'no data yet')})`}
                </Link>
              </Box>
            ))}
          </Stack>
        </Panel>
      ) : null}

      {application.lease_id ? (
        <Panel sx={{ mb: 2.5 }}>
          <Typography variant="body2">
            {t('applicationDetail.hasLease', 'This application has a lease record.')}{' '}
            <Link component={RouterLink} to={`/lease/${application.lease_id}`} underline="hover">
              {t('applicationDetail.viewLease', 'View lease')}
            </Link>
          </Typography>
        </Panel>
      ) : null}

      {awaitingFinalize || application.finalization_state === 'finalizing' ? (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          {t('applicationDetail.creatingLease', 'Selection saved. Creating the lease… this page will update when it finishes.')}
        </Alert>
      ) : null}

      {application.last_finalize_error || application.finalization_state === 'finalize_failed' ? (
        <Panel title={t('applicationDetail.submissionError', 'Submission error')} sx={{ mb: 2.5 }}>
          {application.payment_status === 'paid' ? (
            <Box sx={{ mb: 1.5 }}>
              <StatusChip status="paid" label={t('applicationDetail.paymentSucceeded', 'Payment succeeded')} tone="success" size="small" />
            </Box>
          ) : null}
          {application.last_finalize_error ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'error.main' }}>
              {application.last_finalize_error}
            </Typography>
          ) : null}
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={1.5} sx={{ mt: 1.5 }}>
            <Typography variant="body2">
              {application.payment_status === 'paid'
                ? t(
                    'applicationDetail.paymentSucceededGuidance',
                    'The payment went through. Do not ask the applicant to submit another payment. You can retry lease creation below.'
                  )
                : t('applicationDetail.submissionErrorGuidance', 'Contact TenantPort support to resolve the submission error.')}
            </Typography>
            {application.payment_status === 'paid' && application.finalization_state === 'finalize_failed' && !application.lease_id ? (
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={retryFinalizeMutation.isPending || awaitingFinalize}
                onClick={() => retryFinalizeMutation.mutate()}
                sx={{ flexShrink: 0 }}
              >
                {retryFinalizeMutation.isPending
                  ? t('applicationDetail.retryFinalizePending', 'Queuing…')
                  : t('applicationDetail.retryFinalize', 'Retry lease creation')}
              </Button>
            ) : null}
          </Stack>
        </Panel>
      ) : null}

      <ApplicationSelectionEditor
        key={`${application.application_id}-${application.unit}-${application.term}-${application.move_in_date}-${application.finalization_state}`}
        application={application}
        queryKey={queryKey}
        onFinalizeQueued={() => {
          finalizeQueuedAt.current = Date.now();
          setAwaitingFinalize(true);
        }}
      />

      <Panel title={t('applicationDetail.applicant', 'Applicant')} sx={{ mb: 2.5 }}>
        <Grid container rowSpacing={2.25} columnSpacing={2}>
          <Field label={t('applicationDetail.email', 'Email')}>{blank(application.email)}</Field>
          <Field label={t('applicationDetail.mobilePhone', 'Mobile Phone')}>{blank(application.mobile_phone)}</Field>
          <Field label={t('applicationDetail.householdStatus', 'Household Status (role)')}>{blank(application.household_status)}</Field>
          <Field label={t('applicationDetail.property', 'Property')}>{blank(application.property_name)}</Field>
          <Field label={t('applicationDetail.unit', 'Unit')}>{blank(application.unit)}</Field>
          <Field label={t('applicationDetail.moveInDate', 'Move-in Date')}>{formatDate(application.move_in_date)}</Field>
          <Field label={t('applicationDetail.leaseTerm', 'Lease Term')}>{application.term ? `${application.term} months` : 'N.A.'}</Field>
          <Field label={t('applicationDetail.rent', 'Rent')}>{formatMoney(application.rent)}</Field>
          <Field label={t('applicationDetail.registered', 'Registered in TenantPort')}>
            {relatedUser ? t('common.success', 'Success') : t('common.pending', 'Pending')}
          </Field>
          <Field label={t('applicationDetail.lastLogin', 'Last Login')}>{relatedUser?.lastLogin ? formatDateTime(relatedUser.lastLogin) : 'N.A.'}</Field>
          {relatedUser ? (
            <Field label={t('applicationDetail.password', 'Password')}>
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                  ************
                </Typography>
                <Link component="button" type="button" onClick={() => setShowResetPasswordConfirmModal(true)}>
                  {t('common.reset', 'Reset')}
                </Link>
                <Typography variant="body2" component="span" color="text.secondary">
                  |
                </Typography>
                <Link component="button" type="button" onClick={() => setShowResetPasswordModal(true)}>
                  {t('common.change', 'Change')}
                </Link>
              </Stack>
            </Field>
          ) : (
            <Field label={t('applicationDetail.invite', 'Invite')}>
              <Stack spacing={0.75} alignItems="flex-start">
                <Link
                  component="button"
                  type="button"
                  disabled={inviteApplicationMutation.isPending || !application.email}
                  onClick={() => inviteApplicationMutation.mutate()}
                >
                  {inviteApplicationMutation.isPending
                    ? t('applicationDetail.sendingInvite', 'Sending…')
                    : t('applicationDetail.sendInvite', 'Send invitation email')}
                </Link>
                {inviteUrl ? (
                  <>
                    <Link component="button" type="button" onClick={handleCopyInviteLink}>
                      {t('applicationDetail.copyInviteLink', 'Copy invite link')}
                    </Link>
                    <Alert severity="warning" sx={{ fontWeight: 500, py: 0.5 }}>
                      {t(
                        'applicationDetail.inviteLinkEmailHint',
                        'Only needed if the applicant did not receive the invite email. Email this link — do not text it; it is long and often breaks in SMS.'
                      )}
                    </Alert>
                  </>
                ) : null}
              </Stack>
            </Field>
          )}
          {resumeUrl ? (
            <Field label={t('applicationDetail.resumeLink', 'Resume application')}>
              <Stack spacing={0.75} alignItems="flex-start">
                <Link component="button" type="button" onClick={handleCopyResumeLink}>
                  {t('applicationDetail.copyResumeLink', 'Copy resume link')}
                </Link>
                <Alert severity="info" sx={{ fontWeight: 500, py: 0.5 }}>
                  {t(
                    'applicationDetail.resumeLinkHint',
                    'Send this so the applicant continues where they left off. Prefer email — long links often break in SMS.'
                  )}
                </Alert>
              </Stack>
            </Field>
          ) : null}
        </Grid>
      </Panel>

      {roster.length > 1 ? (
        <Panel title={t('applicationDetail.roster', 'Household roster (as submitted)')} sx={{ mb: 2.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('applicationDetail.rosterName', 'Name')}</TableCell>
                <TableCell>{t('applicationDetail.rosterRole', 'Role / Relationship')}</TableCell>
                <TableCell>{t('applicationDetail.rosterEmail', 'Email')}</TableCell>
                <TableCell>{t('applicationDetail.rosterOwnRecord', 'Own record?')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roster.map((member, i) => (
                <TableRow key={`${member.email || member.name}-${i}`}>
                  <TableCell>
                    {blank(member.name)}
                    {member.adult === false ? ` ${t('applicationDetail.minorSuffix', '(minor)')}` : ''}
                  </TableCell>
                  <TableCell>{blank(member.role)}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{member.email || '—'}</TableCell>
                  <TableCell>
                    {member.isPrimary ? t('applicationDetail.rosterThisApp', 'This application') : t('applicationDetail.rosterNotYet', 'Not yet')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('applicationDetail.rosterNote', 'From the application itself — separate records and logins are not created until the lease finalizes.')}
          </Typography>
        </Panel>
      ) : null}

      <Panel
        title={t('applicationDetail.status', 'Application status')}
        action={
          canCancel ? (
            <Button size="small" color="error" variant="outlined" onClick={() => setCancelOpen(true)} disabled={cancelMutation.isPending}>
              {t('applicationDetail.cancelApplication', 'Cancel application')}
            </Button>
          ) : null
        }
      >
        <Grid container rowSpacing={2.25} columnSpacing={2}>
          <Field label={t('applicationDetail.submissionStatus', 'Submission Status')}>
            {application.submission_status ? (
              <StatusChip status={application.submission_status} label={humanize(application.submission_status)} size="small" />
            ) : (
              'N.A.'
            )}
          </Field>
          <Field label={t('applicationDetail.paymentStatus', 'Payment Status')}>
            {application.payment_status ? <StatusChip status={application.payment_status} label={humanize(application.payment_status)} size="small" /> : 'N.A.'}
          </Field>
          <Field label={t('applicationDetail.finalizationState', 'Finalization State')}>
            {application.finalization_state ? (
              <StatusChip
                status={application.finalization_state}
                label={humanize(application.finalization_state)}
                tone={stateTone(application.finalization_state)}
                size="small"
              />
            ) : (
              'N.A.'
            )}
          </Field>
          <Field label={t('applicationDetail.leaseRecord', 'Lease Record')}>{application.lease_id ? 'Yes' : 'No'}</Field>
          <Field label={t('applicationDetail.applicationDate', 'Application Date')}>{formatDate(application.application_date)}</Field>
          <Field label={t('applicationDetail.lastUpdated', 'Last Updated')}>{formatDateTime(application.updated_date)}</Field>
          {/* ID Verification (Plaid) — not a Field, since the value is a block (chip + reason + link) that can't nest inside Field's <p>. */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              {t('applicationDetail.idVerification', 'ID Verification')}
            </Typography>
            {latestIdv ? (
              <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                <StatusChip status={latestIdv.status} label={idvStatusLabel(latestIdv.status)} tone={idvStatusTone(latestIdv.status)} size="small" />
                {latestIdvFailureDescription ? (
                  <Typography variant="caption" color="error.main">
                    {latestIdvFailureDescription}
                  </Typography>
                ) : null}
                {latestIdv.externalSessionId ? (
                  <Link href={plaidIdvSessionUrl(latestIdv.externalSessionId)} target="_blank" rel="noopener" underline="hover" sx={{ fontSize: 12 }}>
                    {idvSessions.length > 1
                      ? t('applicationDetail.viewInPlaidMany', 'View in Plaid ({{count}} sessions)', { count: idvSessions.length })
                      : t('applicationDetail.viewInPlaid', 'View in Plaid')}
                  </Link>
                ) : null}
              </Stack>
            ) : (
              <Typography variant="body2">N.A.</Typography>
            )}
          </Grid>
        </Grid>
      </Panel>

      {showResetPasswordConfirmModal && relatedUser && (
        <ResetPasswordConfirmModal
          onClose={() => setShowResetPasswordConfirmModal(false)}
          onSubmit={() => {
            resetPasswordEmailMutation.mutate({ userId: relatedUser.id });
            setShowResetPasswordConfirmModal(false);
          }}
        />
      )}
      {showResetPasswordModal && relatedUser && (
        <ResetPasswordModal userId={relatedUser.id} residentName={application.name || relatedUser.email} onClose={() => setShowResetPasswordModal(false)} />
      )}
      <ConfirmDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={t('applicationDetail.cancelConfirmTitle', 'Cancel this application?')}
        body={t(
          'applicationDetail.cancelConfirmBody',
          'This will cancel the application so the applicant can start over. It does not withdraw anything in ResMan and does not refund a payment.'
        )}
        cancelLabel={t('common.keep', 'Keep application')}
        confirmLabel={t('applicationDetail.cancelConfirm', 'Cancel application')}
        destructive
        loading={cancelMutation.isPending}
        onConfirm={() => cancelMutation.mutate()}
      />

      <Panel sx={{ mt: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="h4">{t('applicationDetail.submittedApplication', 'Submitted Application')}</Typography>
          {submitted?.role || application.household_status ? (
            <StatusChip
              status={submitted?.role || application.household_status}
              tone={roleTone(submitted?.role || application.household_status)}
              size="small"
            />
          ) : null}
          {submitted?.status || application.submission_status ? (
            <StatusChip
              status={submitted?.status || application.submission_status}
              label={humanize(submitted?.status || application.submission_status)}
              size="small"
            />
          ) : null}
          {submitted?.updatedDate ? (
            <Typography variant="caption" color="text.secondary">
              {t('applicationDetail.lastUpdated', 'Last Updated')} {formatDateTime(submitted.updatedDate)}
            </Typography>
          ) : null}
        </Stack>
        {submitted?.applicationData ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('applicationDetail.submittedApplicationSubtitle', 'Read-only view of everything this applicant entered, exactly as submitted.')}
            </Typography>
            <ApplicationSteps
              app={{ role: submitted.role, applicationData: submitted.applicationData }}
              isAffordable={submitted.isAffordable}
              idvSessions={idvSessions}
            />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t(
              'applicationDetail.submittedApplicationEmpty',
              "This applicant hasn't entered any application details on this record. It may be a duplicate — check their other applications below."
            )}
          </Typography>
        )}
      </Panel>

      {siblings.length > 0 ? (
        <Panel title={t('applicationDetail.otherApplications', "Applicant's other applications")} sx={{ mt: 2.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('applicationDetail.otherAppsProperty', 'Property')}</TableCell>
                <TableCell>{t('applicationDetail.otherAppsUnit', 'Unit')}</TableCell>
                <TableCell>{t('applicationDetail.otherAppsStatus', 'Status')}</TableCell>
                <TableCell>{t('applicationDetail.otherAppsData', 'Has data')}</TableCell>
                <TableCell>{t('applicationDetail.otherAppsLease', 'Lease')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {siblings.map(sib => (
                <TableRow key={sib.application_id}>
                  <TableCell>{blank(sib.property_name)}</TableCell>
                  <TableCell>{sib.unit ? sib.unit : '—'}</TableCell>
                  <TableCell>
                    <StatusChip
                      status={sib.pending_reason}
                      label={humanize(sib.pending_reason)}
                      tone={REASON_TONES[sib.pending_reason] || 'neutral'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{sib.has_data ? t('applicationDetail.otherAppsYes', 'Yes') : '—'}</TableCell>
                  <TableCell>{sib.lease_id ? t('applicationDetail.otherAppsYes', 'Yes') : '—'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1.5} justifyContent="flex-end" flexWrap="wrap">
                      <Link component={RouterLink} to={`/applications/${sib.application_id}`} underline="hover">
                        {t('applicationDetail.otherAppsView', 'View application')}
                      </Link>
                      {sib.lease_id ? (
                        <Link component={RouterLink} to={`/lease/${sib.lease_id}`} underline="hover">
                          {t('applicationDetail.viewLease', 'View lease')}
                        </Link>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t(
              'applicationDetail.otherAppsNote',
              'Same applicant, other application records. A completed or leased row here usually means this one is a duplicate.'
            )}
          </Typography>
        </Panel>
      ) : null}
    </Box>
  );
}
